import type { CustomDomainTarget } from '@transported-labs/dxp-sdk/customDomain';
import { isCueDomain, resolveCustomDomain } from '@transported-labs/dxp-sdk/customDomain';

// Kept apart from the app on purpose: it runs before the main bundle is requested, so it may import
// nothing but the SDK's custom-domain module (no store, no common barrels, no SDK core).

const GEO_FENCING_GAME_ID = 'geo-fencing';
// Mirrors utils/isGeoFencingEntry.ts, which reads the URL through a snapshot this entry must not take.
const GEO_FENCING_ENTRY_PARAM = 'geoFencing';
// Mirrors utils/isLandingEntry.ts: the static landing QR goes to the platform landing screen, not the domain's.
const LANDING_ENTRY_PARAM = 'landing';

/** Only a bare visit to the domain — what the old Front Door redirect rule matched — has a destination. */
const isBareVisit = (params: URLSearchParams): boolean =>
  !params.has('clientId') &&
  !params.has('gameId') &&
  !params.has(GEO_FENCING_ENTRY_PARAM) &&
  !params.has(LANDING_ENTRY_PARAM) &&
  !params.has('preview') &&
  ['', '#', '#/'].includes(location.hash);

const withDestination = (params: URLSearchParams, target: CustomDomainTarget): URLSearchParams => {
  params.set('clientId', target.clientId);

  // As the landing screen does: geo fencing has no instance, the fences pick the engagement.
  if (target.gameId === GEO_FENCING_GAME_ID) {
    params.set(GEO_FENCING_ENTRY_PARAM, 'true');
    return params;
  }

  params.set('gameId', target.gameId);
  target.configurationId && params.set('configurationId', target.configurationId);

  return params;
};

/**
 * Sends a bare visit to a client's custom domain where super-admin points that domain (AB#38841).
 * Resolves true when the page is being replaced, so the caller does not start the app.
 *
 * The lookup is the one `XBackend.init` would make anyway, and the SDK shares it: a domain without a
 * destination costs no extra request, and after the redirect the URL carries the clientId, so the
 * SDK makes none at all.
 */
export const applyCustomDomainEntry = async (): Promise<boolean> => {
  const params = new URLSearchParams(location.search);

  if (isCueDomain() || !isBareVisit(params)) return false;

  const target = await resolveCustomDomain();

  if (!target?.gameId) return false;

  location.replace(`${location.pathname}?${withDestination(params, target).toString()}`);

  return true;
};
